// Renders the in-app placement card preview for a campaign.
// The card varies by offer type:
//
//   - 'none'        → real CourseGridCard(s) for each featured course
//                     (matches what NetworkUpsells renders to students)
//   - 'discount'    → DiscountCard — code + percent/amount + validity, with the
//                     featured courses listed as the scope below
//   - 'invitation'  → InvitationCard — course name, "You're invited" framing,
//                     expiry, accept-button (visually similar to the email)
//
// Used from the wizard (StepContent) and the detail page (ContentTab).

import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CourseGridCard, Flex, SimpleButton, Typo, Icon } from '../../../ui';
import { CHANNEL, OFFER } from '../constants';
import { MOCK_COURSES_FOR_FILTER } from '../mockData';

const CardsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  & > * {
    flex: 0 1 320px;
  }
`;

const PromoCard = styled.div`
  flex: 0 1 360px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${({ $from }) => $from} 0%, ${({ $to }) => $to} 100%);
  color: #fff;
  box-shadow: ${({ theme }) => theme.shadows.BOX_SHADOW_SMALL};
`;

const Eyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.85;
`;

const BigPercent = styled.div`
  font-size: 56px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const CodeBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px dashed rgba(255, 255, 255, 0.6);
  font-family: ${({ theme }) => theme.fonts?.MONO_FONT ?? 'monospace'}, monospace;
  font-weight: 700;
  letter-spacing: 0.08em;
`;

const ScopeList = styled.div`
  font-size: 13px;
  opacity: 0.9;
`;

const Validity = styled.div`
  font-size: 12px;
  opacity: 0.8;
`;

const ActionRow = styled.div`
  margin-top: auto;
  display: flex;
`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso; }
};

const courseNameFor = (id) => MOCK_COURSES_FOR_FILTER.find((c) => c.id === id)?.name ?? id;
const courseFor = (id) => MOCK_COURSES_FOR_FILTER.find((c) => c.id === id);

const DiscountCard = ({ campaign, theme }) => {
  const { t } = useTranslation();
  const featured = campaign?.content?.networkUpsell?.featuredCourseIds ?? [];
  const code = campaign?.discount?.code;
  const percent = campaign?.discount?.percentage;
  const validUntil = campaign?.discount?.validUntil;

  return (
    <PromoCard $from={theme.colors.PRIMARY} $to={theme.colors.MARKER} theme={theme}>
      <Eyebrow>{t('campaigns.upsell.limitedOffer', 'Limited offer')}</Eyebrow>
      <BigPercent>{percent ? `${percent}% OFF` : t('campaigns.upsell.discount', 'Discount')}</BigPercent>
      {code && <div><CodeBox theme={theme}>{code}</CodeBox></div>}
      {featured.length > 0 && (
        <ScopeList>
          {t('campaigns.upsell.appliesTo', 'Applies to:')} {featured.map(courseNameFor).join(' · ')}
        </ScopeList>
      )}
      {validUntil && (
        <Validity>{t('campaigns.upsell.validUntil', 'Valid until {{date}}', { date: formatDate(validUntil) })}</Validity>
      )}
      <ActionRow>
        <SimpleButton variant='tertiary'>{t('campaigns.upsell.useCode', 'Use code')}</SimpleButton>
      </ActionRow>
    </PromoCard>
  );
};

const InvitationCard = ({ campaign, theme }) => {
  const { t } = useTranslation();
  const course = courseFor(campaign?.invitation?.courseId);
  const expiresAt = campaign?.invitation?.expiresAt;

  return (
    <PromoCard $from={theme.colors.MARKER} $to={theme.colors.PRIMARY} theme={theme}>
      <Eyebrow>
        <Flex gap='6' align='center'>
          <Icon.Gift size='small' color='#fff' />
          {t('campaigns.upsell.invitedBadge', "You're invited")}
        </Flex>
      </Eyebrow>
      <Typo.ContentSmallHeader style={{ color: '#fff' }}>
        {course?.name ?? t('campaigns.upsell.courseInvitation', 'Course invitation')}
      </Typo.ContentSmallHeader>
      {course?.description && (
        <ScopeList>{course.description}</ScopeList>
      )}
      {expiresAt && (
        <Validity>{t('campaigns.upsell.expiresOn', 'Expires {{date}}', { date: formatDate(expiresAt) })}</Validity>
      )}
      <ActionRow>
        <SimpleButton variant='tertiary'>{t('campaigns.upsell.accept', 'Accept invitation')}</SimpleButton>
      </ActionRow>
    </PromoCard>
  );
};

const UpsellCardPreview = ({ campaign, theme }) => {
  const channels = campaign?.channels ?? [];
  if (!channels.includes(CHANNEL.NETWORK_UPSELL)) return null;

  const featured = campaign?.content?.networkUpsell?.featuredCourseIds ?? [];
  const offer = campaign?.offer ?? OFFER.NONE;

  if (offer === OFFER.DISCOUNT) {
    return <CardsRow><DiscountCard campaign={campaign} theme={theme} /></CardsRow>;
  }

  if (offer === OFFER.INVITATION) {
    return <CardsRow><InvitationCard campaign={campaign} theme={theme} /></CardsRow>;
  }

  // No offer → render the real CourseGridCard, one per featured course.
  const courses = featured.map(courseFor).filter(Boolean);
  if (courses.length === 0) return null;
  return (
    <CardsRow>
      {courses.map((course) => (
        <CourseGridCard
          key={course.id}
          courseInfo={course}
          buyLabel='View course'
          onCardClick={() => {}}
        />
      ))}
    </CardsRow>
  );
};

export default UpsellCardPreview;
